import { ComponentRef, Injectable } from '@angular/core';

import { delayWhen, Observable, of, Subject, take, takeWhile, timer } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { AsyncJob, AsyncJobService, AsyncJobStatus } from '@recruitee/async-job-data';
import { filterBoolean, Helpers } from '@recruitee/common';
import { NotificationsController, NotificationStyle } from '@recruitee/user-interface';

import { AsyncJobViewNotificationModel } from '../async-job-view.types';
import { AsyncJobsNotificationComponent } from '../components/async-jobs-notification/async-jobs-notification.component';
import { AsyncJobConfigService } from '../configs/async-job-config.service';
import { AsyncJobViewModelService } from './async-job-view-model.service';

@Injectable({ providedIn: 'root' })
export class AsyncJobNotificationsService {
  public notificationClick$ = new Subject<AsyncJob>();

  private onScreenNotifications: Map<string, ComponentRef<AsyncJobsNotificationComponent>> =
    new Map();

  constructor(
    private asyncJobConfig: AsyncJobConfigService,
    private notificationsCtrl: NotificationsController,
    private asyncJobService: AsyncJobService,
    private asyncJobViewModelService: AsyncJobViewModelService,
  ) {}

  public isVisible(job: AsyncJob) {
    return this.onScreenNotifications.has(job.uuid);
  }

  public isEmpty() {
    return this.onScreenNotifications.size === 0;
  }

  public show(job: AsyncJob): void {
    if (this.isVisible(job)) return;

    const jobConfig = this.asyncJobConfig.get(job);
    const jobState$ = this.asyncJobService.getJob$(job.uuid);

    const notificationRef: ComponentRef<AsyncJobsNotificationComponent> =
      this.notificationsCtrl.custom(AsyncJobsNotificationComponent);

    notificationRef.instance.setJobDisplayStream(this.getNotificationData$(job.uuid));

    notificationRef.instance.closeNotification
      .pipe(takeUntil(notificationRef.instance.ngOnDestroy$))
      .subscribe(this.closeNotification.bind(this));

    notificationRef.instance.actionButtonClick
      .pipe(
        switchMap(() => jobState$),
        take(1),
      )
      .subscribe(currentJobState => jobConfig.onNotificationActionButtonClick?.(currentJobState!));

    notificationRef.instance.notificationClick
      .pipe(
        switchMap(() => jobState$),
        take(1),
        takeUntil(notificationRef.instance.ngOnDestroy$),
      )
      .subscribe(currentJobState => {
        this.notificationClick$.next(currentJobState!);
        this.onNotificationClick(currentJobState!.uuid);
      });

    this.onScreenNotifications.set(job.uuid, notificationRef);
  }

  public closeNotification(uuid: string) {
    const notificationRef = this.onScreenNotifications?.get(uuid);

    if (notificationRef) {
      notificationRef.destroy();
      notificationRef.instance.containerComponent!.refresh();
    }
  }

  public onNotificationClick(uuid: string) {
    this.closeNotification(uuid);
  }

  public destroy(job: AsyncJob) {
    this.onScreenNotifications.delete(job.uuid);
  }

  private getJobNotificationStyle(job: AsyncJob): NotificationStyle | undefined {
    const config = this.asyncJobConfig.get(job);
    switch (job.status) {
      case AsyncJobStatus.WORKING:
        return config.workingNotificationStyle;
      case AsyncJobStatus.START:
        return config.startNotificationStyle;
      case AsyncJobStatus.IDLE:
        return config.idleNotificationStyle;
      case AsyncJobStatus.FINISHED:
        return config.finishedNotificationStyle;
      default:
        return undefined;
    }
  }

  private getNotificationData$(jobUuid: string): Observable<AsyncJobViewNotificationModel> {
    return this.asyncJobService.getJob$(jobUuid).pipe(
      filterBoolean,
      map(job => {
        const candidates = Helpers.camelizeKeys(job?.payload?.candidates);
        const jobViewModel = this.asyncJobViewModelService.create(job);
        const { notificationConfig } = this.asyncJobConfig.get(job);
        const style = this.getJobNotificationStyle(job);
        return {
          ...jobViewModel,
          candidates: candidates && candidates.length ? [...candidates] : undefined,
          notificationConfig: {
            ...notificationConfig,
            ...(style && { style }),
          },
        };
      }),
      delayWhen(job => (job.done ? timer(1000) : of(null))),
      takeWhile(job => !job.done, true),
    );
  }
}
